import apiClient from '../../../services/apiClient';

const isAbsoluteUrl = (value) =>
  /^(https?:)?\/\//i.test(value) ||
  /^(data|blob):/i.test(value);

const compactParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== '',
    ),
  );

export const unwrapStudentData = (payload) => {
  if (
    payload &&
    typeof payload === 'object' &&
    Object.prototype.hasOwnProperty.call(
      payload,
      'data',
    )
  ) {
    return payload.data;
  }

  return payload;
};

export const resolveMediaUrl = (value) => {
  if (
    !value ||
    typeof value !== 'string'
  ) {
    return '';
  }

  if (isAbsoluteUrl(value)) {
    return value.startsWith('//')
      ? `${window.location.protocol}${value}`
      : value;
  }

  try {
    const configuredBase =
      apiClient.defaults.baseURL ||
      window.location.origin;

    const origin = new URL(
      configuredBase,
      window.location.origin,
    ).origin;

    return `${origin}/${value.replace(
      /^\/+/,
      '',
    )}`;
  } catch (error) {
    console.warn(
      'Unable to resolve media URL.',
      value,
      error,
    );

    return value;
  }
};

const extractUrlValue = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value !== 'object') {
    return '';
  }

  const candidate =
    value.file_url ||
    value.url ||
    value.download_url ||
    value.file ||
    value.path ||
    value.file_path ||
    value.storage_path;

  if (candidate === value) {
    return '';
  }

  return extractUrlValue(candidate);
};

export const getDocumentUrl = (document) => {
  const rawUrl = extractUrlValue(
    document?.file_url ||
      document?.file_record ||
      document,
  );

  return resolveMediaUrl(rawUrl);
};

export const getDocumentName = (document) => {
  const fileRecord =
    document?.file_record || {};

  const explicitName =
    document?.file_name ||
    fileRecord.original_name ||
    fileRecord.file_name ||
    fileRecord.filename ||
    fileRecord.name;

  if (explicitName) {
    return explicitName;
  }

  const rawUrl = extractUrlValue(
    document?.file_url ||
      fileRecord,
  );

  if (!rawUrl) {
    return 'Document';
  }

  try {
    return (
      decodeURIComponent(
        rawUrl
          .split('/')
          .pop()
          .split('?')[0],
      ) || 'Document'
    );
  } catch (error) {
    return 'Document';
  }
};

const flattenValidationErrors = (
  value,
  path = '',
) => {
  if (
    value === undefined ||
    value === null
  ) {
    return [];
  }

  if (typeof value === 'string') {
    return [
      path
        ? `${path}: ${value}`
        : value,
    ];
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return [
      path
        ? `${path}: ${String(value)}`
        : String(value),
    ];
  }

  if (Array.isArray(value)) {
    return value.flatMap(
      (item, index) => {
        if (
          typeof item === 'string' ||
          typeof item === 'number'
        ) {
          return flattenValidationErrors(
            item,
            path,
          );
        }

        const itemPath = path
          ? `${path}.${index}`
          : String(index);

        return flattenValidationErrors(
          item,
          itemPath,
        );
      },
    );
  }

  if (typeof value === 'object') {
    return Object.entries(value).flatMap(
      ([key, nestedValue]) => {
        const nextPath =
          key === 'non_field_errors'
            ? path
            : path
              ? `${path}.${key}`
              : key;

        return flattenValidationErrors(
          nestedValue,
          nextPath,
        );
      },
    );
  }

  return [];
};

const normalizeErrorPath = (path) => {
  let normalizedPath = String(
    path || '',
  )
    .replace(
      /\.non_field_errors$/,
      '',
    )
    .replace(
      /^non_field_errors$/,
      '',
    );

  /*
   * The backend may identify the current address using
   * the address type instead of the array index.
   */
  normalizedPath = normalizedPath.replace(
    /^addresses\.CURRENT/,
    'addresses.0',
  );

  normalizedPath = normalizedPath.replace(
    /^addresses\.PERMANENT/,
    'addresses.1',
  );

  return normalizedPath || 'root.server';
};

const firstMessage = (value) => {
  if (
    value === undefined ||
    value === null
  ) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstMessage(item);

      if (message) {
        return message;
      }
    }

    return '';
  }

  if (typeof value === 'object') {
    for (const nestedValue of Object.values(
      value,
    )) {
      const message = firstMessage(
        nestedValue,
      );

      if (message) {
        return message;
      }
    }
  }

  return '';
};

const flattenFieldErrors = (
  value,
  path = '',
  output = {},
) => {
  if (
    value === undefined ||
    value === null
  ) {
    return output;
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    output[normalizeErrorPath(path)] =
      String(value);

    return output;
  }

  if (Array.isArray(value)) {
    const containsObjects = value.some(
      (item) =>
        item &&
        typeof item === 'object' &&
        !Array.isArray(item),
    );

    if (!containsObjects) {
      const message = firstMessage(value);

      if (message) {
        output[normalizeErrorPath(path)] =
          message;
      }

      return output;
    }

    value.forEach((item, index) => {
      const itemPath = path
        ? `${path}.${index}`
        : String(index);

      flattenFieldErrors(
        item,
        itemPath,
        output,
      );
    });

    return output;
  }

  if (typeof value === 'object') {
    Object.entries(value).forEach(
      ([key, nestedValue]) => {
        if (key === 'non_field_errors') {
          const message =
            firstMessage(nestedValue);

          if (message) {
            output[
              normalizeErrorPath(path)
            ] = message;
          }

          return;
        }

        const nextPath = path
          ? `${path}.${key}`
          : key;

        flattenFieldErrors(
          nestedValue,
          nextPath,
          output,
        );
      },
    );
  }

  return output;
};

export const getApiFieldErrors = (
  error,
) => {
  const payload =
    error?.response?.data;

  const validationSource =
    payload?.errors ||
    payload?.data?.errors ||
    null;

  if (!validationSource) {
    return {};
  }

  return flattenFieldErrors(
    validationSource,
  );
};

export const getApiErrorMessages = (
  error,
) => {
  const payload =
    error?.response?.data;

  const validationSource =
    payload?.errors ||
    payload?.data?.errors;

  if (validationSource) {
    const validationMessages =
      flattenValidationErrors(
        validationSource,
      );

    if (validationMessages.length > 0) {
      return validationMessages;
    }
  }

  const directMessage =
    payload?.detail ||
    payload?.error ||
    payload?.message ||
    error?.message;

  if (
    typeof directMessage === 'string' &&
    directMessage.trim()
  ) {
    return [directMessage.trim()];
  }

  return [];
};

export const getApiErrorMessage = (
  error,
  fallback = 'Something went wrong.',
) => {
  const messages =
    getApiErrorMessages(error);

  return messages.length > 0
    ? messages.join(' ')
    : fallback;
};

export const getStudentIdFromResponse = (
  response,
) => {
  const payload =
    unwrapStudentData(response);

  return (
    payload?.id ||
    response?.id ||
    null
  );
};

export const studentApi = {
  getStudents: async (
    schoolId,
    params = {},
  ) => {
    const normalizedParams =
      typeof params === 'number'
        ? {
            page: params,
          }
        : {
            ...params,
          };

    /*
     * Accept both frontend camelCase names and the
     * backend query parameter names.
     */
    const requestParams = compactParams({
      q:
        normalizedParams.q ??
        normalizedParams.search,

      status:
        normalizedParams.status,

      gender:
        normalizedParams.gender,

      academic_year:
        normalizedParams.academic_year ??
        normalizedParams.academicYearId ??
        normalizedParams.academic_year_id,

      class_level:
        normalizedParams.class_level ??
        normalizedParams.classLevelId ??
        normalizedParams.class_level_id,

      section:
        normalizedParams.section ??
        normalizedParams.sectionId ??
        normalizedParams.section_id,

      admission_date_from:
        normalizedParams.admission_date_from ??
        normalizedParams.admissionDateFrom,

      admission_date_to:
        normalizedParams.admission_date_to ??
        normalizedParams.admissionDateTo,

      ordering:
        normalizedParams.ordering,

      page:
        normalizedParams.page,

      page_size:
        normalizedParams.page_size ??
        normalizedParams.pageSize,
    });

    const response = await apiClient.get(
      `/schools/${schoolId}/students/`,
      {
        params: requestParams,
      },
    );

    return response.data;
  },

  searchStudents: async (
    schoolId,
    query,
    params = {},
  ) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/students/`,
      {
        params: compactParams({
          q: String(query || '').trim(),
          page: params.page,
          page_size:
            params.page_size ??
            params.pageSize,
          status: params.status,
          academic_year:
            params.academic_year ??
            params.academicYearId,
          class_level:
            params.class_level ??
            params.classLevelId,
          section:
            params.section ??
            params.sectionId,
        }),
      },
    );

    const payload =
      unwrapStudentData(response.data);

    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.results)) {
      return payload.results;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    return [];
  },

  getStudentById: async (
    schoolId,
    studentId,
  ) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/students/${studentId}/`,
    );

    return response.data;
  },

  createStudent: async (
    schoolId,
    studentData,
  ) => {
    const response = await apiClient.post(
      `/schools/${schoolId}/students/`,
      studentData,
    );

    return response.data;
  },

  updateStudent: async (
    schoolId,
    studentId,
    studentData,
  ) => {
    const response = await apiClient.patch(
      `/schools/${schoolId}/students/${studentId}/update/`,
      studentData,
    );

    return response.data;
  },

  updateStudentStatus: async (
    schoolId,
    studentId,
    status,
  ) => {
    const response = await apiClient.patch(
      `/schools/${schoolId}/students/${studentId}/status/`,
      {
        status,
      },
    );

    return response.data;
  },

  uploadStudentPhoto: async (
    schoolId,
    studentId,
    file,
  ) => {
    if (
      typeof File === 'undefined' ||
      !(file instanceof File)
    ) {
      throw new Error(
        'Invalid photo file selected.',
      );
    }

    const formData = new FormData();

    formData.append(
      'photo',
      file,
      file.name,
    );

    const response = await apiClient.patch(
      `/schools/${schoolId}/students/${studentId}/update/`,
      formData,
    );

    return response.data;
  },

  uploadStudentDocument: async (
    schoolId,
    studentId,
    documentData,
    {
      documentType,
      documentNumber = '',
    } = {},
  ) => {
    let formData = documentData;

    if (
      typeof FormData !== 'undefined' &&
      !(documentData instanceof FormData)
    ) {
      if (
        typeof File === 'undefined' ||
        !(documentData instanceof File)
      ) {
        throw new Error(
          'Invalid student document selected.',
        );
      }

      formData = new FormData();

      formData.append(
        'file',
        documentData,
        documentData.name,
      );

      formData.append(
        'document_type',
        documentType,
      );

      formData.append(
        'document_number',
        documentNumber,
      );
    }

    const response = await apiClient.post(
      `/schools/${schoolId}/students/${studentId}/documents/upload/`,
      formData,
    );

    return response.data;
  },
};

export default studentApi;